import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useCookie } from './useCookie'

type ConsentCategories = 'consent' | 'necessary' | 'functional' | 'analytics' | 'performance' | 'advertisement'

type CkyConsentApiResponse = {
  activeLaw: 'gdpr' | 'ccpa'
  categories: {
    [key in ConsentCategories]: boolean
  }
  isUserActionCompleted: boolean
  consentID: string
  languageCode: string
}

export const parseConsentCookie = (cookie: string = ''): { [key: string]: string } | undefined => {
  if (!cookie) return undefined
  const parsed = cookie?.split(',').reduce<{ [key: string]: string }>((acc, pair) => {
    const [key, value] = pair.split(':')
    acc[key] = value
    return acc
  }, {})
  return parsed
}

const yesValueCheck = (value: string | undefined, truthyDefault: boolean | undefined) => {
  return value !== undefined ? value === 'yes' : truthyDefault
}

export const checkHasConsent = (
  consentOptions: { [key: string]: string } = {},
  privacyCookieOptions: { [key: string]: string } = {},
  categoryName: ConsentCategories,
  overrideDefault?: boolean
) => {
  const { [categoryName]: value } = consentOptions

  if (consentOptions?.action?.length) {
    // this means we were able to read the cookie and it already takes into account the default value based on gdpr/ccpa
    return yesValueCheck(value, false)
  }

  // Try to check the cookieyes api to get the active law to set a default
  const cookieyesConsentApiVal =
    typeof window !== 'undefined' ? ((window as any)?.getCkyConsent?.() as CkyConsentApiResponse) : undefined
  const apiActiveLaw = cookieyesConsentApiVal ? cookieyesConsentApiVal?.activeLaw : undefined

  if (apiActiveLaw) {
    const categoryValue = cookieyesConsentApiVal?.categories?.[categoryName]
    // this means cookieyes api was loadable and there was a value for activeLaw
    return categoryValue != null ? categoryValue : apiActiveLaw !== 'gdpr'
  }

  // Try to check the knock_privacy cookie to get the policyType to set a default
  if (privacyCookieOptions?.policyType) {
    return yesValueCheck(value, privacyCookieOptions.policyType !== 'gdpr')
  }

  return overrideDefault || undefined
}

const fetchGeo = async () => {
  const response = await fetch(`/api/geo`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  const data = await response.json()
  return data
}

/**
 * useConsentCheck react hook
 * @param cookieName - the name of the cookie to check
 * @param category - the category of the cookie to check
 * @param defaultTrue - default value to return for this check if the cookie is not set
 */
export const useConsentCheck = ({
  cookieYesName,
  knockPrivacyName,
  categoryName,
  defaultTrue = false,
}: {
  cookieYesName: string
  knockPrivacyName: string
  categoryName: ConsentCategories
  defaultTrue?: boolean
}) => {
  const [consentCookie] = useCookie(cookieYesName, '', true)
  const [privacyCookie] = useCookie(knockPrivacyName, '', true)
  const consentOptions = useMemo(() => parseConsentCookie(consentCookie), [consentCookie])
  const privacyCookieOptions = useMemo(() => parseConsentCookie(privacyCookie), [privacyCookie])
  const [overrideDefault, setOverrideDefault] = useState<boolean | undefined>(defaultTrue)
  // useCookie only reads cookies after mount, so on the first render every
  // cookie looks absent. Report consent as unknown until that read lands.
  // This also keeps checkHasConsent's window.getCkyConsent lookup out of the
  // hydration render, where the server can never match it. A layout effect so
  // it lands in the same synchronous re-render as the cookie values.
  const [hasReadCookies, setHasReadCookies] = useState(false)
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- must land in the same synchronous re-render as useCookie
    setHasReadCookies(true)
  }, [])

  const hasConsent = useMemo(
    () =>
      hasReadCookies ? checkHasConsent(consentOptions, privacyCookieOptions, categoryName, overrideDefault) : undefined,
    [hasReadCookies, consentOptions, privacyCookieOptions, categoryName, overrideDefault]
  )

  useEffect(() => {
    async function fetchData() {
      try {
        await fetchGeo()
      } catch {
        // The geo lookup is best effort. It fails when the page navigates away
        // mid-request or a blocker drops it. Re-check anyway: CookieYes may have
        // loaded in the meantime. Not logged, since these aborts are routine.
      }
      const recheckValue = checkHasConsent(consentOptions, privacyCookieOptions, categoryName, overrideDefault)
      setOverrideDefault(recheckValue)
    }
    if (hasReadCookies && hasConsent === undefined) {
      fetchData()
    }
  }, [hasReadCookies, hasConsent, setOverrideDefault])

  return [hasConsent, consentOptions]
}
